import { useRef } from "react";
import { Link } from "react-router-dom";
import type { AssetItem } from "../api/types";
import { madeForLine } from "../copy/cast";
import { Pill } from "../ui";
import { categoryWord, isSurfaceCategory } from "./categories";
import { sizeCm } from "./format";
import { preloadViewer } from "./inspector";
import { Star } from "./Star";

const AREA_WORDS: Record<string, string> = {
  flat: "Flat",
  cylinder: "Wraps around",
};

/** A figure's height in words ("168 cm tall"); `null` without its size. */
export function figureHeight(
  size: [number, number, number] | null | undefined,
): string | null {
  return size ? `${Math.round(size[2] * 100)} cm tall` : null;
}

/** The one line of facts under a card's name; `null` when there is none. */
export function factLine(item: AssetItem): string | null {
  if (item.stale) return `No longer fits ${item.shape_name ?? ""}`.trim();
  if (item.kind === "shape") return item.size_m ? sizeCm(item.size_m) : null;
  if (item.kind === "variant") return `Look of ${item.shape_name ?? ""}`.trim();
  if (item.kind === "figure") return figureHeight(item.size_m);
  if (item.kind === "material")
    return isSurfaceCategory(item.category)
      ? categoryWord(item.category)
      : null;
  const word = AREA_WORDS[item.region_type ?? ""];
  // Pixel sizes are written without thousands separators.
  return word && item.px ? `${word}, ${item.px[0]} × ${item.px[1]}` : null;
}

function CardBody({ item }: { item: AssetItem }) {
  const fact = factLine(item);
  return (
    <>
      <span className="block aspect-square w-full overflow-hidden rounded-lg bg-slate-100">
        {item.thumb_url ? (
          <img
            src={item.thumb_url}
            alt={item.name}
            loading="lazy"
            className="h-full w-full object-cover"
          />
        ) : null}
      </span>
      <span className="mt-2 flex items-start justify-between gap-2">
        <span
          className="min-w-0 break-words text-sm font-semibold text-slate-800"
          data-testid="asset-name"
        >
          {item.name}
        </span>
        {item.tier === "org" ? (
          <Pill size="sm" tone="purple" className="shrink-0">
            Yours
          </Pill>
        ) : null}
      </span>
      {fact ? (
        <span
          className="mt-0.5 block text-sm text-slate-600"
          data-testid="asset-fact"
        >
          {fact}
        </span>
      ) : null}
      {item.made_for?.length ? (
        <span
          className="mt-0.5 block break-words text-xs text-slate-500"
          data-testid="asset-made-for"
        >
          {madeForLine(item.made_for)}
        </span>
      ) : null}
    </>
  );
}

/** The ⋯ menu in a card's corner, built like the shell's user menu. It sits
 *  beside the card's link, not inside it, so opening it does not navigate. */
function CardMenu(props: { item: AssetItem; onDelete(item: AssetItem): void }) {
  const { item } = props;
  const menu = useRef<HTMLDetailsElement>(null);
  return (
    <details ref={menu} className="absolute right-1 top-1">
      <summary
        className="flex h-11 w-11 cursor-pointer list-none items-center justify-center rounded-full bg-white/90 text-lg font-bold leading-none text-slate-700 shadow-card hover:bg-white"
        aria-label={`More actions for ${item.name}`}
      >
        ⋯
      </summary>
      <div
        className="tb-card tb-card-plain absolute right-0 z-10 mt-1 flex w-36 flex-col gap-1 p-2 text-sm"
        role="menu"
      >
        <button
          type="button"
          role="menuitem"
          className="min-h-[44px] rounded px-2 py-1.5 text-left hover:bg-slate-100 sm:min-h-0"
          onClick={() => {
            if (menu.current) menu.current.open = false;
            props.onDelete(item);
          }}
        >
          Delete
        </button>
      </div>
    </details>
  );
}

/** How a card behaves while the grid is a picker ([library-section §10.5]). */
export interface CardSelect {
  pressed: boolean;
  disabled: boolean;
  /** Why it cannot be pressed. */
  title?: string;
  onSelect(item: AssetItem): void;
}

/** One thing in the 3D library: its picture, its name, one fact. A card
 *  opens its inspector page. A look that no longer fits its object is dimmed
 *  and leads nowhere. The star sits on the picture's top-left corner, beside
 *  the card's link and not inside it; a look that no longer fits has none.
 *  One of the organisation's own has a ⋯ menu with **Delete** when the grid
 *  gives it `onDelete`; a look that no longer fits keeps the menu, so it can
 *  still be deleted. With `select` the card is a toggle button instead: no
 *  link, no star and no menu. */
export function AssetCard({
  item,
  onDelete,
  select,
}: {
  item: AssetItem;
  onDelete?: (item: AssetItem) => void;
  select?: CardSelect;
}) {
  if (select) {
    const disabled = select.disabled || item.stale;
    return (
      <button
        type="button"
        aria-pressed={select.pressed}
        disabled={disabled}
        title={select.disabled ? select.title : undefined}
        className={[
          "block w-full cursor-pointer rounded-lg border-0 bg-transparent p-1 text-left",
          "disabled:cursor-not-allowed disabled:opacity-50",
          select.pressed ? "ring-2 ring-brand" : "ring-1 ring-transparent",
        ].join(" ")}
        data-testid="asset-card"
        onClick={() => select.onSelect(item)}
      >
        <CardBody item={item} />
      </button>
    );
  }
  const body = item.stale ? (
    <div className="block w-full text-left opacity-50" data-testid="asset-card">
      <CardBody item={item} />
    </div>
  ) : (
    <Link
      to={`/assets/${item.kind}/${item.id}`}
      className="block w-full rounded-lg text-left no-underline"
      data-testid="asset-card"
      // The page this leads to draws in 3D: its code starts on its way now.
      onPointerEnter={preloadViewer}
      onFocus={preloadViewer}
    >
      <CardBody item={item} />
    </Link>
  );
  const menu = item.tier === "org" && onDelete !== undefined;
  if (item.stale && !menu) return body;
  return (
    <div className="relative">
      {body}
      {item.stale ? null : (
        <Star
          kind={item.kind}
          id={item.id}
          on={item.favorite}
          className="absolute left-1 top-1"
        />
      )}
      {menu ? <CardMenu item={item} onDelete={onDelete} /> : null}
    </div>
  );
}
